import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const EMPTY = { price: '', originalPrice: '', label: '', description: '', active: true };

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null); // code
  const [form, setForm] = useState(EMPTY);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get('/admin/plans')
      .then(r => setPlans(Array.isArray(r.data) ? r.data : []))
      .catch(e => setError(e.response?.data?.message || 'Failed to load plans'));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.code);
    setForm({
      price: String(p.price ?? ''),
      originalPrice: String(p.originalPrice ?? p.price ?? ''),
      label: p.label || '',
      description: p.description || '',
      active: p.active !== false,
    });
    setMsg('');
    setError('');
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!editing) return;
    setLoading(true);
    setMsg('');
    setError('');
    try {
      const body = {
        price: Number(form.price),
        originalPrice: Number(form.originalPrice),
        label: form.label,
        description: form.description,
        active: !!form.active,
      };
      await api.put(`/admin/plans/${editing}`, body);
      setMsg(`${editing} updated successfully.`);
      setEditing(null);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Plan pricing</h1>
          <p className="text-sm text-slate-500 mt-1">
            Publish sell prices and compare-at (struck-out) amounts. Users see these on the subscribe page.
          </p>
        </div>
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-800">← Admin home</Link>
      </div>

      {msg && (
        <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 text-sm">{msg}</div>
      )}
      {error && (
        <div className="mb-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 text-sm">{error}</div>
      )}

      <div className="space-y-4">
        {plans.map((p) => {
          const isEdit = editing === p.code;
          return (
            <div
              key={p.code}
              className={`rounded-2xl border bg-white shadow-sm p-5 ${
                isEdit ? 'border-slate-900 ring-2 ring-slate-900/10' : 'border-slate-100'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{p.code}</p>
                  <p className="text-lg font-semibold mt-0.5">{p.label}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {p.months} month{p.months !== 1 ? 's' : ''} · sort {p.sortOrder}
                    {!p.active && <span className="ml-2 text-rose-500 font-medium">Inactive</span>}
                  </p>
                </div>
                {!isEdit && (
                  <div className="text-right">
                    <p className="text-2xl font-bold tabular-nums">${Number(p.price).toFixed(0)}</p>
                    {p.originalPrice != null && Number(p.originalPrice) > Number(p.price) && (
                      <p className="text-sm text-slate-400">
                        <span className="line-through">${Number(p.originalPrice).toFixed(0)}</span>
                        <span className="ml-1 text-emerald-600 text-xs font-semibold">Great value</span>
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => startEdit(p)}
                      className="mt-2 text-sm font-medium text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline"
                    >
                      Edit prices
                    </button>
                  </div>
                )}
              </div>

              {isEdit && (
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    <span className="text-slate-500">Sell price ($)</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-slate-500">Original / compare-at ($)</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.originalPrice}
                      onChange={(e) => setForm((f) => ({ ...f, originalPrice: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                    <span className="text-[11px] text-slate-400">Shown struck-out next to sell price</span>
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="text-slate-500">Label</span>
                    <input
                      type="text"
                      value={form.label}
                      onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </label>
                  <label className="block text-sm sm:col-span-2">
                    <span className="text-slate-500">Description</span>
                    <input
                      type="text"
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm sm:col-span-2">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    <span>Active (shown on subscribe page)</span>
                  </label>
                  <div className="sm:col-span-2 flex flex-wrap gap-2 mt-1">
                    <button
                      type="button"
                      disabled={loading}
                      onClick={save}
                      className="rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50"
                    >
                      {loading ? 'Saving…' : 'Save prices'}
                    </button>
                    <button
                      type="button"
                      onClick={cancel}
                      className="rounded-xl bg-slate-100 text-slate-700 px-4 py-2 text-sm font-medium hover:bg-slate-200"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {plans.length === 0 && !error && (
        <p className="text-sm text-slate-400 text-center py-8">No plans found. Restart the backend to seed defaults.</p>
      )}
    </div>
  );
}
